import {
    CSSProperties,
    FC,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import { IonCard, IonIcon } from "@ionic/react";
import { arrowRedo, football } from "ionicons/icons";
import { useTranslation } from "react-i18next";
import { isEqual } from "lodash";
import { MatchClaims } from "refsix-js-models";
import { ClaimKind, claimOne, releaseOne } from "./goalClaims";
import {
    claimHaptic,
    claimRefusedHaptic,
    claimReleasedHaptic,
} from "./claimHaptics";
import "./ClaimRows.css";

const UNDO_MS = 4000;

interface ClaimRowsProps {
    claims: MatchClaims;
    // The team's score: goals and assists together can't go past it
    teamGoals: number;
    onChange: (claims: MatchClaims) => void;
    // The user's totals from this season's other matches
    season: MatchClaims;
}

// The last thing the user did, so only that row, pip and number animate
interface Action {
    seq: number;
    kind?: ClaimKind;
    claimed?: boolean;
    allClaimed?: boolean;
    refused?: boolean;
}

interface Flight {
    seq: number;
    kind: ClaimKind;
    style: CSSProperties;
}

const ROWS: {
    kind: ClaimKind;
    icon: string;
    testId: string;
    countId: string;
}[] = [
    { kind: "goals", icon: football, testId: "Goal", countId: "claimedGoals" },
    {
        kind: "assists",
        icon: arrowRedo,
        testId: "Assist",
        countId: "claimedAssists",
    },
];

/**
 * Goals and assists rows with a Claim button each, under the score (SCORZA-110).
 * The reward stays on the card: the ball flies to its pip, the row pops and the
 * phone buzzes. A short-lived Undo, or tapping a lit pip, stands in for a minus.
 */
export const ClaimRows: FC<ClaimRowsProps> = ({
    claims,
    teamGoals,
    onChange,
    season,
}) => {
    const { t } = useTranslation();
    const [action, setAction] = useState<Action>({ seq: 0 });
    const [undo, setUndo] = useState<{
        kind: ClaimKind;
        before: MatchClaims;
        after: MatchClaims;
    }>();
    const [flight, setFlight] = useState<Flight>();
    const bodyRef = useRef<HTMLDivElement>(null);
    const buttonRefs = useRef<{ [kind: string]: HTMLButtonElement | null }>({});
    const pipRefs = useRef<(HTMLElement | null)[]>([]);

    const { goals, assists } = claims;
    const allClaimed = teamGoals > 0 && goals + assists >= teamGoals;
    // Pips: my goals, then my assists, then the team's other goals
    const pips: (ClaimKind | "open")[] = [
        ...Array(goals).fill("goals"),
        ...Array(assists).fill("assists"),
        ...Array(Math.max(teamGoals - goals - assists, 0)).fill("open"),
    ];
    const arriving = action.claimed
        ? action.kind === "goals"
            ? goals - 1
            : goals + assists - 1
        : -1;

    // Undo lasts a few seconds, and only while the claims are still the ones it made
    useEffect(() => {
        if (!undo) return;
        const timer = setTimeout(() => setUndo(undefined), UNDO_MS);
        return () => clearTimeout(timer);
    }, [undo]);
    const canUndo = undo && isEqual(undo.after, claims);

    // Send a ball from the Claim button to the pip it fills
    useLayoutEffect(() => {
        const body = bodyRef.current;
        const from = action.kind && buttonRefs.current[action.kind];
        const to = pipRefs.current[arriving];
        if (!action.claimed || !action.kind || !body || !from || !to) return;
        const origin = body.getBoundingClientRect(),
            a = from.getBoundingClientRect(),
            b = to.getBoundingClientRect();
        const x = a.left + a.width / 2 - origin.left,
            y = a.top + a.height / 2 - origin.top;
        setFlight({
            seq: action.seq,
            kind: action.kind,
            style: {
                left: x,
                top: y,
                "--dx": `${b.left + b.width / 2 - origin.left - x}px`,
                "--dy": `${b.top + b.height / 2 - origin.top - y}px`,
            } as CSSProperties,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action.seq]);

    const claim = (kind: ClaimKind) => {
        const next = claimOne(claims, kind, teamGoals);
        if (!next) {
            setAction({ seq: action.seq + 1, refused: true });
            claimRefusedHaptic();
            return;
        }
        const nowAll = next.goals + next.assists >= teamGoals;
        setAction({
            seq: action.seq + 1,
            kind,
            claimed: true,
            allClaimed: nowAll,
        });
        setUndo({ kind, before: claims, after: next });
        onChange(next);
        claimHaptic(kind, next.goals, nowAll);
    };

    const release = (kind: ClaimKind, restored: MatchClaims) => {
        setAction({ seq: action.seq + 1, kind, claimed: false });
        setUndo(undefined);
        onChange(restored);
        claimReleasedHaptic();
    };

    const celebrate = action.claimed && action.allClaimed;
    const ticked = (kind: ClaimKind) =>
        action.kind === kind && action.claimed ? "roll-up" : undefined;
    return (
        <IonCard
            className={`edit-match-card claim-rows${
                celebrate ? " all-claimed" : ""
            }`}
            data-testid="claimRows"
        >
            <div className="claim-rows-body" ref={bodyRef}>
                <div className="claim-rows-head">
                    <span className="claim-rows-title">
                        {t("editMatch.claimRack.title")}
                    </span>
                    <div
                        key={action.refused ? action.seq : "pips"}
                        className={`claim-pips${celebrate ? " shine" : ""}${
                            action.refused ? " refused" : ""
                        }`}
                    >
                        {pips.map((pip, i) => (
                            <button
                                type="button"
                                key={
                                    i === arriving
                                        ? `arriving-${action.seq}`
                                        : i
                                }
                                ref={(el) => (pipRefs.current[i] = el)}
                                className={`claim-pip ${pip}${
                                    i === arriving ? " arriving" : ""
                                }`}
                                disabled={pip === "open"}
                                aria-label={
                                    pip === "open"
                                        ? undefined
                                        : t(
                                              pip === "goals"
                                                  ? "editMatch.claimRack.releaseGoal"
                                                  : "editMatch.claimRack.releaseAssist"
                                          )
                                }
                                onClick={() =>
                                    pip !== "open" &&
                                    release(pip, releaseOne(claims, pip))
                                }
                                data-testid={`claimPip-${i}`}
                                data-claim={pip}
                            >
                                {pip !== "open" && <IonIcon icon={football} />}
                            </button>
                        ))}
                    </div>
                </div>

                {ROWS.map(({ kind, icon, testId, countId }) => {
                    const value = claims[kind];
                    const acted = action.kind === kind;
                    return (
                        <div
                            key={kind}
                            className={`claim-row ${kind} level-${Math.min(
                                value,
                                3
                            )}${value ? " has" : ""}`}
                            data-testid={`claimRow${testId}`}
                        >
                            {acted && action.claimed && (
                                <span
                                    key={action.seq}
                                    className="claim-row-sweep"
                                />
                            )}
                            <span
                                key={acted ? `icon-${action.seq}` : "icon"}
                                className={`claim-row-icon${
                                    acted && action.claimed ? " pop" : ""
                                }`}
                            >
                                <IonIcon icon={icon} />
                            </span>
                            <span className="claim-row-label">
                                {t(`editMatch.claimRack.${kind}`)}
                            </span>
                            <span className="claim-row-count">
                                <span
                                    key={value}
                                    className={
                                        acted
                                            ? action.claimed
                                                ? "roll-up"
                                                : "roll-down"
                                            : undefined
                                    }
                                    data-testid={countId}
                                >
                                    {value}
                                </span>
                            </span>
                            <button
                                type="button"
                                ref={(el) => (buttonRefs.current[kind] = el)}
                                className={`claim-row-button${
                                    allClaimed ? " maxed" : ""
                                }`}
                                onClick={() => claim(kind)}
                                data-testid={`claim${testId}`}
                            >
                                {t("editMatch.claimRack.claim")}
                            </button>
                        </div>
                    );
                })}

                <div className="claim-rows-foot">
                    <span
                        className={action.refused ? "lit" : undefined}
                        data-testid="claimSeason"
                    >
                        {action.refused ? (
                            t("editMatch.claimRack.allClaimed", {
                                count: teamGoals,
                            })
                        ) : (
                            <>
                                {t("editMatch.claimRack.season")}{" "}
                                <b
                                    key={`g${goals}`}
                                    className={ticked("goals")}
                                >
                                    {season.goals + goals}
                                </b>{" "}
                                {t("editMatch.claimRack.seasonGoals", {
                                    count: season.goals + goals,
                                })}{" "}
                                ·{" "}
                                <b
                                    key={`a${assists}`}
                                    className={ticked("assists")}
                                >
                                    {season.assists + assists}
                                </b>{" "}
                                {t("editMatch.claimRack.seasonAssists", {
                                    count: season.assists + assists,
                                })}
                            </>
                        )}
                    </span>
                    {canUndo && (
                        <button
                            type="button"
                            className="claim-rows-undo"
                            onClick={() => release(undo.kind, undo.before)}
                            data-testid="claimUndo"
                        >
                            {t("editMatch.claimRack.undo")}
                        </button>
                    )}
                </div>

                {flight && (
                    <span
                        key={flight.seq}
                        className={`claim-flyer ${flight.kind}`}
                        style={flight.style}
                        onAnimationEnd={() => setFlight(undefined)}
                    >
                        <IonIcon icon={football} />
                    </span>
                )}
            </div>
        </IonCard>
    );
};
